/** 共円の TypeScript 表現。mixed-engine の serde 形と一致させる。 */
export type Side = 'First' | 'Second';

export interface KyoenPoint {
  row: number;
  col: number;
}

export type KyoenEndReason = 'Concyclic' | 'Collinear' | 'NoMove' | 'InvalidClaim';

export interface KyoenOutcome {
  winner: Side | null;
  reason: KyoenEndReason;
  circle: [KyoenPoint, KyoenPoint, KyoenPoint, KyoenPoint] | null;
}

export interface KyoenState {
  size: number;
  stones: KyoenPoint[];
  side: Side;
  outcome?: KyoenOutcome | null;
}

export type KyoenMove = { Place: KyoenPoint } | { Claim: { points: KyoenPoint[] } };

export function legalKey(row: number, col: number): string {
  return `${row}-${col}`;
}

export function pointLabel(point: KyoenPoint, size: number): string {
  return `${String.fromCharCode(65 + point.col)}${size - point.row}`;
}

export type KyoenGeometry =
  | { kind: 'circle'; cx: number; cy: number; radius: number }
  | { kind: 'line'; x1: number; y1: number; x2: number; y2: number };

/**
 * 終局に記録された4点を通る図形を描くための座標。勝敗・合法手は再判定しない。
 * 円の場合、共線の3点を避けて外接円を求める。表示だけなので平方根を使用できる。
 */
export function outcomeGeometry(outcome: KyoenOutcome | null | undefined): KyoenGeometry | null {
  const points = outcome?.circle;
  if (!points || outcome.reason === 'NoMove') return null;

  if (outcome.reason === 'Collinear') {
    let ends: [KyoenPoint, KyoenPoint] = [points[0], points[1]];
    let longest = 0;
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        const length = (points[i].col - points[j].col) ** 2 + (points[i].row - points[j].row) ** 2;
        if (length > longest) {
          longest = length;
          ends = [points[i], points[j]];
        }
      }
    }
    if (longest === 0) return null;
    const [a, b] = ends;
    const dx = ((b.col - a.col) / Math.sqrt(longest)) * 0.35;
    const dy = ((b.row - a.row) / Math.sqrt(longest)) * 0.35;
    return { kind: 'line', x1: a.col - dx, y1: a.row - dy, x2: b.col + dx, y2: b.row + dy };
  }

  const a = points[0];
  for (let i = 1; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const b = points[i];
      const c = points[j];
      const bx = b.col - a.col;
      const by = b.row - a.row;
      const cx = c.col - a.col;
      const cy = c.row - a.row;
      const determinant = 2 * (bx * cy - by * cx);
      if (determinant === 0) continue;
      const bq = bx * bx + by * by;
      const cq = cx * cx + cy * cy;
      const x = (cy * bq - by * cq) / determinant;
      const y = (bx * cq - cx * bq) / determinant;
      return { kind: 'circle', cx: a.col + x, cy: a.row + y, radius: Math.hypot(x, y) };
    }
  }
  return null;
}
